<template>
  <div>
    <v-dialog v-model="h.is_open" class="w-full max-w-3xl dialog-top" @keydown.enter="handle_enter">
      <v-card class="dialog-top-content">
        <v-card-title class="">
            <slot>
                <div class="text-center py-2 whitespace-normal dialog-top-title">
                    {{ h.message }}
                    <p v-if="h.hint" class="text-base font-normal text-neutral-600">{{ h.hint }}</p>
                </div>
            </slot>
        </v-card-title>
        <div v-if="['prompt', 'prompt_required', 'field'].includes(h.kind)" class="px-4">
          <v-text-field v-focus v-model="h.input" :label="h.label" />
        </div>
        <div v-else-if="h.kind === 'field_tel'" class="px-4">
          <v-text-field v-focus v-model="h.input" :label="h.label" type="tel" />
        </div>
        <div v-else-if="h.kind === 'number'" class="px-4 flex align-center justify-center gap-2">
          <v-btn icon @click="h.input > 0 && h.input--">
            <Icons.TIRemove class=""></Icons.TIRemove>
          </v-btn>
          <v-text-field v-focus v-model.number="h.input" type="number" min="0" class="max-w-[6rem]" hide-details />
          <v-btn icon @click="h.input++">
            <Icons.TIAdd class=""></Icons.TIAdd>
          </v-btn>
        </div>
        <div v-else-if="h.kind === 'date'" class="px-4">
          <v-text-field v-model="h.input" type="date" :min="get_today()" />
        </div>
        <div v-else-if="h.kind === 'time'" class="px-4">
          <v-text-field v-model="h.input" type="time" />
        </div>
        <div v-else-if="h.kind === 'date_time'" class="px-4">
          <div class="flex align-center gap-2">
            <v-text-field v-model="input_date" type="date" :min="get_today()" hide-details />
            <v-text-field v-model="input_time" type="time" hide-details />
          </div>
        </div>
        <div v-else-if="h.kind === 'select'" class="px-4">
          <v-select
            v-model="h.input"
            :label="h.label"
            :items="h.items"
            :item-value="h.item_value"
            :item-title="h.item_title"
          />
        </div>
        <div v-else-if="h.kind === 'list'" class="px-2">
          <v-list density="compact">
            <v-list-item
              v-for="item in h.items"
              :key="item[h.item_value]"
              @click="select_item(item)"
            >
                <p class="text-center">{{ item[h.item_title] }}</p>
            </v-list-item>
          </v-list>
        </div>
        <div v-if="!['list'].includes(h.kind)" class="flex flex-row-reverse p-4 justify-end -mx-2 dialog-top-buttons">
          <template v-if="h.kind === 'alert'">
            <v-btn class="mx-2 flex-grow h-12" @click="close">{{ t('taxilib.alert.ok') }}</v-btn>
          </template>
          <template v-if="h.kind === 'info'">
              <v-btn class="mx-2 flex-grow h-12" @click="close">{{ t('taxilib.alert.close') }}</v-btn>
          </template>
          <template v-if="['confirm', 'prompt', 'number', 'date', 'time', 'date_time', 'select'].includes(h.kind)">
            <v-btn class="mx-2 flex-grow bg-red-600 text-white h-12 dialog-top-red-button" @click="confirm">{{ t('taxilib.alert.yes') }}</v-btn>
            <v-btn class="mx-2 flex-grow h-12" @click="deny">{{ t('taxilib.alert.no') }}</v-btn>
          </template>
          <template v-if="['prompt_required'].includes(h.kind)">
            <v-btn class="mx-2 flex-grow bg-red-600 text-white h-12 dialog-top-red-button" @click="confirm" :disabled="!h.input">{{ t('taxilib.alert.yes') }}</v-btn>
            <v-btn class="mx-2 flex-grow h-12" @click="deny">{{ t('taxilib.alert.no') }}</v-btn>
          </template>
          <div v-if="['field', 'field_tel'].includes(h.kind)" class="grid grid-cols-2 w-full">
            <v-btn class="mx-2 flex-grow h-12" @click="deny">{{ t('taxilib.alert.cancel') }}</v-btn>
            <v-btn class="mx-2 flex-grow h-12" @click="confirm" :disabled="!h.input">{{ t('taxilib.alert.ok') }}</v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { reactive, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { log, vFocus, Icons, get_today, rules } from 'taxi-lib';

const { t, te, locale } = useI18n();
const router = useRouter();
const input_date = ref('');
const input_time = ref('');

const h = reactive({
  is_open: false,
  kind: 'alert',
  message: '',
  input: '',
  label: '',
  items: [],  
  item_value: 'value',
  item_title: 'title',
  hint: '',
});
let result_resolve = null;

const show = (message, kind, label = '', value = '', items = [], item_value = 'value', item_title = 'title', hint = '') =>
{
  return new Promise((resolve) =>
  {
    result_resolve = resolve;
    h.message = message;
    h.input = value;
    h.label = label,
    h.kind = ['alert', 'confirm', 'info', 'prompt', 'prompt_required', 'number', 'date', 'time', 'date_time', 'select', 'list', 'field', 'field_tel'].includes(kind) ? kind : 'alert';
    h.items = items;
    h.item_value = item_value;
    h.item_title = item_title;
    h.hint = hint;
    h.is_open = true;
  });
};

watch(() => h.is_open, (new_value) =>
{
  if (!new_value)
  {
    close();
  }
  if (h.kind === 'date_time') {
    const [d, t = '00:00'] = h.input.split(' ');
    input_date.value = d;
    input_time.value = t.slice(0, 5);
  }
});

watch([input_date, input_time], ([d, t]) => {
  if (h.kind === 'date_time') {
    h.input = `${d} ${t}`
  }
});

const close = () =>
{
  h.is_open = false;
  if (result_resolve != null)
  {
    result_resolve(null);
    result_resolve = null;
  }
};

const confirm = () =>
{
  h.is_open = false;
  if (result_resolve != null)
  {
    result_resolve(['prompt', 'prompt_required', 'number', 'date', 'time', 'date_time', 'select', 'field', 'field_tel'].includes(h.kind) ? h.input : true);
    result_resolve = null;
  }
};

const deny = () =>
{
  h.is_open = false;
  if (result_resolve != null)
  {
    if (['prompt', 'prompt_required', 'number', 'date', 'time', 'date_time', 'select', 'field', 'field_tel'].includes(h.kind)) {
      result_resolve(null);
    }
    else {
      result_resolve(false);
    }
    result_resolve = null;
  }
};

const select_item = (item) => {
  h.is_open = false;
  if (result_resolve != null) {
    result_resolve(item[h.item_value]);
    result_resolve = null;
  }
};

function to_login()
{
  const currentPath = window.location.pathname;
  const to = router.resolve({ name: 'Login', query: { next: currentPath } });
  window.location.assign(to.href);
}

const check_key = async (tkey, fallback = 'taxilib.alert.api.error') => {
  if (te(tkey)) {
    await show(t(tkey));
  } else {
    log.w("api_alert no such key", tkey);
    await show(t(fallback));
  }
};

async function api_alert(resp, callback = null)
{
  if (resp == null)
  {
    await show(t('taxilib.alert.api.conn_error'));
    return false;
  }
  if(callback !== null)
  {
    const key = callback(resp);
    if (key) {
      if(te(key))
      {
        await show(t(key));
        return false;
      }
      log.w("api_alert callback no such key", key);
    }
  }
  if (!resp.error)
  {
    log.d("api_alert return true", resp);
    return true;
  }
/*
  if (resp.error_data === 'ACCESS_DENIED')
  {
    to_login();
    return false;
  }
*/
  const tkey = 'taxilib.alert.api.error_data.' + resp.error_data;
  await check_key(tkey);
  return false;
}

async function api_alert_client(resp, callback = null)
{
  if (resp == null)
  {
    await show(t('taxilib.alert.api.conn_error'));
    return false;
  }
  if(callback !== null)
  {
    const key = callback(resp);
    if (key) {
      if(te(key))
      {
        await show(t(key));
        return false;
      }
      log.w("api_alert callback no such key", key);
    }
  }
  if (!resp.error)
  {
    log.d("api_alert return true", resp);
    return true;
  }
  const tkey = 'taxilib.alert.api.error_data_client.' + resp.error_data;
  await check_key(tkey, 'taxilib.alert.api.error_data_client.EXCEPTION');
  return false;
}

const handle_enter = () => {
  if (['prompt', 'prompt_required', 'number', 'date', 'time', 'date_time', 'select', 'field', 'field_tel'].includes(h.kind)) {
    confirm();
  }
};

defineExpose({
  alert: {
    show,
    api_alert,
    api_alert_client,
    to_login,
    close,
    confirm,
    deny,
  }
});
</script>
<style scoped>
.dialog-top {
    align-items: flex-start;
    margin-top: 2rem;
}
</style>
